import React from 'react';
import { Card, CardContent, Box, Typography, Chip, IconButton, Tooltip } from '@mui/material';
import { AutoAwesome } from '@mui/icons-material';

const STATUS_COLORS = {
  green: { bg: '#E8F5E9', text: '#2E7D32', dot: '#4CAF50' },
  red: { bg: '#FFEBEE', text: '#C62828', dot: '#EF5350' },
  yellow: { bg: '#FFFDE7', text: '#F57F17', dot: '#FFC107' },
  blue: { bg: '#E3F2FD', text: '#0D47A1', dot: '#42A5F5' },
};

export default function MetricCard({
  title,
  value,
  subtitle,
  icon: Icon,
  iconColor = '#1565C0',
  status = 'green',       // 'green' | 'red' | 'yellow' | 'blue'
  insight,
  trend,
  onAskAI = null,
  valueSx = {},
}) {
  const sc = STATUS_COLORS[status] || STATUS_COLORS.green;

  return (
    <Card sx={{ height: '100%', minHeight: 210, display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flex: 1, p: 2.5, display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
          <Typography variant="body2" color="text.secondary" fontWeight={600} textTransform="uppercase" letterSpacing="0.5px" fontSize="0.72rem">
            {title}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {onAskAI && (
              <Tooltip title="Ask AI about this">
                <IconButton size="small" onClick={onAskAI}
                  sx={{ color: 'primary.main', opacity: 0.5, '&:hover': { opacity: 1 }, p: 0.25 }}>
                  <AutoAwesome sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            )}
            {Icon && (
              <Box sx={{ width: 36, height: 36, borderRadius: '10px', background: `${iconColor}18`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Icon sx={{ fontSize: 20, color: iconColor }} />
              </Box>
            )}
          </Box>
        </Box>

        {/* Main value */}
        <Typography variant="h4" fontWeight={800} color="text.primary" sx={{ mb: 0.5, lineHeight: 1.1, overflowWrap: 'break-word', ...valueSx }}>
          {value}
        </Typography>

        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {subtitle}
          </Typography>
        )}

        {trend && (
          <Typography variant="body2" fontWeight={600} color={trend.startsWith('+') ? 'success.main' : 'error.main'}>
            {trend}
          </Typography>
        )}

        {/* Spacer pushes insight chip to card bottom */}
        <Box sx={{ flex: 1 }} />

        {/* AI Insight chip */}
        {insight && (
          <Chip
            label={insight}
            size="small"
            sx={{
              mt: 1.5,
              bgcolor: sc.bg,
              color: sc.text,
              fontWeight: 600,
              fontSize: '0.72rem',
              height: 'auto',
              borderRadius: '6px',
              '& .MuiChip-label': { whiteSpace: 'normal', py: 0.5, px: 1 },
            }}
          />
        )}
      </CardContent>
    </Card>
  );
}
